import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import en from './translations/en'
import fi from './translations/fi'
import swe from './translations/swe'
import de from './translations/de'
import no from './translations/no'
import fr from './translations/fr'
import nl from './translations/nl'
import es from './translations/es'
import Language from './enums/Language'
import { isValidLanguage } from './utils/language'
import { DEFAULT_LANGUAGE, TRANSLATION_NAMESPACES } from './constants'
import { datadogRum } from '@datadog/browser-rum'
import type { TranslationResource } from './types/Translation'

const getFallbackLng = (): Language => {
  const language = window.localStorage.getItem('language') ?? DEFAULT_LANGUAGE

  if (isValidLanguage(language)) {
    return language
  }

  return DEFAULT_LANGUAGE
}

const warnedKeys = new Set<string>([])

// README: If you make changes to this file, by either adding languages
// or namespaces, then add them also to the `checkMissingTranslationKeys.js` script
// and to the 18n parser config file, to make the missing translations script work

// Here is a prompt to generate other language translations from a json file:
// Translate this JSON to German, English, Spanish, Finnish, French, Dutch, Norwegian (Bokmål) and Swedish:
const resources = {
  fi,
  en,
  swe,
  de,
  no,
  fr,
  nl,
  es
} satisfies Record<Language, TranslationResource>

i18n.use(initReactI18next).init({
  ns: TRANSLATION_NAMESPACES,
  defaultNS: 'common',
  resources,
  fallbackLng: getFallbackLng(),
  interpolation: {
    escapeValue: false
  },
  saveMissing: true,
  missingKeyHandler: (lng, ns, key, _fallbackValue) => {
    const uniqueKey = `${lng}:${ns}:${key}`

    if (!warnedKeys.has(uniqueKey)) {
      const message = `Missing translation key: "${key}" in namespace "${ns}" for language "${lng}"`

      console.warn(`🚨 ${message}`)
      warnedKeys.add(uniqueKey)

      if (import.meta.env.PROD) {
        datadogRum.addError(new Error(message), {
          type: 'missing_translation',
          key,
          namespace: ns,
          language: lng
        })
      }
    }
  }
})
